import { type FC, useMemo, useState } from 'react';

import { PaymentMethodEnum } from '@/__generated_graphql__/graphql';
import { BodyModal } from '@/components/AdvertisePayments/components/Body/Body';
import {
  getCurrentService,
  mapPaidServicesWithOptions,
  mapPaymentMethods
} from '@/components/AdvertisePayments/components/ExtraContent/ExtraContent.helpers';
import {
  CardId,
  ExtraContentModalProps,
  ServiceTypeMap,
  ViewMethod
} from '@/components/AdvertisePayments/components/ExtraContent/ExtraContent.types';
import { ModalExtraContentPaymentWay } from '@/components/AdvertisePayments/components/ExtraContent/PaymentWay';
import { ModalExtraContentServicePeriod } from '@/components/AdvertisePayments/components/ExtraContent/ServicePeriod';
import { FooterModal } from '@/components/AdvertisePayments/components/Footer/Footer';
import { FOOTER_MODALS_TYPE } from '@/components/AdvertisePayments/components/Footer/Footer.types';
import { HeaderModal } from '@/components/AdvertisePayments/components/Header/Header';
import { HEADER_TYPE_MODALS } from '@/components/AdvertisePayments/components/Header/Header.types';
import { PaymentTypeEnum } from '@/constants/common';

import { DepositSelector } from './DepositSelector';
import * as S from './ExtraContent.styles';

type ExtraContentModalControlledProps = ExtraContentModalProps & {
  selectedPaymentWay: PaymentMethodEnum;
  onChangePaymentWay: (method: PaymentMethodEnum) => void;
};

export const ExtraContentModal: FC<ExtraContentModalControlledProps> = ({
  activeCard,
  activePaidServiceId,
  activePeriodId,
  isExtraContentVisible,
  contentTitle,
  isLoading,
  isAddBalance,
  paidServices,
  setIsLoading,
  onBack,
  onClose,
  setActivePaidServiceId,
  onConfirmPayment,
  setActivePeriodId,
  selectedPaymentWay,
  onChangePaymentWay,
  type
}) => {
  const [isValidPrice, setIsValidPrice] = useState<boolean>(false);
  const serviceType = ServiceTypeMap[activeCard as CardId];

  const currentService = useMemo(
    () => getCurrentService(paidServices, serviceType),
    [paidServices, serviceType]
  );

  const methods: ViewMethod[] = useMemo(
    () => mapPaymentMethods(currentService?.paymentMethods),
    [currentService?.paymentMethods]
  );

  const walletBalance = methods[0]?.balance ?? 0;
  const requiredAmount =
    currentService?.options?.find(option => option.id === activePaidServiceId)
      ?.price ?? 0;

  const isWallet: boolean = selectedPaymentWay === PaymentMethodEnum.Wallet;

  const isNotEnough: boolean = isWallet && walletBalance < requiredAmount;
  const isEmptyData: boolean =
    !activePaidServiceId || (!isAddBalance && !activePeriodId);
  const isInvalidTopUp: boolean = isAddBalance && !isValidPrice;

  const baseDisabledCondition = isEmptyData || isInvalidTopUp;

  const disabled = {
    [PaymentTypeEnum.TOP_UP_BALANCE]: baseDisabledCondition,
    [PaymentTypeEnum.ADVERTISE]: baseDisabledCondition,
    [PaymentTypeEnum.PUBLICATION]: baseDisabledCondition || isNotEnough
  };
  const isDisabled = disabled[type];

  const handlePayClick = async () => {
    if (isDisabled) return;
    setIsLoading(true);
    try {
      await onConfirmPayment(
        selectedPaymentWay,
        activePaidServiceId,
        methods.find(m => m.id === selectedPaymentWay)
          ?.isAppleGooglePayEnabled ?? false,
        isAddBalance
      );
    } finally {
      setIsLoading(false);
    }
  };

  const computedType = isAddBalance
    ? HEADER_TYPE_MODALS.MAIN
    : HEADER_TYPE_MODALS.SECONDARY;

  const { options } = paidServices?.[0] ?? {};
  const computedPaidServices =
    Array.isArray(options) && !isLoading ? options : [];
  const computedMethods = !isLoading ? methods : [];

  return (
    <S.ModalSection $isExtraContentVisible={isExtraContentVisible}>
      <S.ModalContentBox>
        <HeaderModal
          type={computedType}
          contentTitle={contentTitle}
          onBack={onBack}
          onModalClose={onClose}
        />

        <BodyModal>
          {isAddBalance ? (
            <DepositSelector
              selectedPaymentWay={selectedPaymentWay}
              balance={walletBalance}
              options={computedPaidServices}
              setActivePaidServiceId={setActivePaidServiceId}
              setIsValidPrice={setIsValidPrice}
            />
          ) : (
            <ModalExtraContentServicePeriod
              serviceType={serviceType}
              activePeriodId={activePeriodId}
              paidServices={mapPaidServicesWithOptions(paidServices)}
              setActivePeriodId={setActivePeriodId}
              setActivePaidServiceId={setActivePaidServiceId}
            />
          )}

          <ModalExtraContentPaymentWay
            selectedPaymentWay={selectedPaymentWay}
            onChange={onChangePaymentWay}
            methods={computedMethods}
          />
        </BodyModal>
        <FooterModal
          isAddBalance={isAddBalance}
          footerVariant={FOOTER_MODALS_TYPE.PAY_WITH_DISCLAIMER}
          isLoading={isLoading}
          onPay={handlePayClick}
          disabled={isDisabled}
        />
      </S.ModalContentBox>
    </S.ModalSection>
  );
};
